import React, { useEffect, useState } from 'react';
import api from '../api';
import '../styles/StartScreen.css';
import BuildProBrand from './BuildProBrand';
import { AlreadyPlayedPage } from '@precisionpros/game-ui';

const BUILDPRO_COLOR = '#0f766e';
const BUILDPRO_SHADOW = '#115e59';
const BUILDPRO_GAME = { name: 'BuildPro', wordmarkMain: 'Build', wordmarkAccent: 'Pro', badge: 'B', accent: BUILDPRO_COLOR };
const hubUrl = () => (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 'http://localhost:3000' : 'https://games.precisionpros.com';

const shouldPreviewMegaMonday = () => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  const value = params.get('mega_monday');
  return ['1', 'true', 'yes'].includes((value || '').toLowerCase());
};

const shouldPreviewPointsMode = () => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  const value = params.get('points_mode');
  return ['1', 'true', 'yes'].includes((value || '').toLowerCase());
};

const formatTime = (seconds) => {
  if (seconds == null) return '--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

function StartScreen({ onLogin, onStartGame, playerName: appPlayerName = '', isAuthenticated = false }) {
  const [playerName, setPlayerName] = useState('');
  const [password, setPassword] = useState('');
  const [isReserving, setIsReserving] = useState(false);
  const [error, setError] = useState('');
  const [hasPlayedToday, setHasPlayedToday] = useState(false);
  const [checkingPlayed, setCheckingPlayed] = useState(false);
  const [todayLeader, setTodayLeader] = useState(null);
  const [yesterdayWinner, setYesterdayWinner] = useState(null);
  const [dailyGameMode, setDailyGameMode] = useState('standard');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paramName = params.get('player_name');
    const savedName = paramName || localStorage.getItem('playerName') || localStorage.getItem('buildpro_player_name');

    if (savedName) {
      setPlayerName(savedName);
      localStorage.setItem('playerName', savedName);
      localStorage.setItem('buildpro_player_name', savedName);
    }
  }, []);

  useEffect(() => {
    const checkPlayed = async () => {
      const savedName = appPlayerName || localStorage.getItem('playerName') || localStorage.getItem('buildpro_player_name');
      if (!savedName) return;

      setCheckingPlayed(true);
      try {
        const data = await api.hasPlayedToday(savedName);
        setHasPlayedToday(data.played === true || data.has_played_today === true);
      } catch {
        setHasPlayedToday(false);
      } finally {
        setCheckingPlayed(false);
      }
    };

    if (isAuthenticated || appPlayerName || localStorage.getItem('playerName')) {
      checkPlayed();
    }
  }, [appPlayerName, isAuthenticated]);

  useEffect(() => {
    api.dailyLetters({ megaMonday: shouldPreviewMegaMonday(), pointsMode: shouldPreviewPointsMode() })
      .then((data) => setDailyGameMode(data.game_mode || 'standard'))
      .catch(() => setDailyGameMode('standard'));

    api.todayWinner()
      .then((data) => setTodayLeader(data.player_name ? data : null))
      .catch(() => setTodayLeader(null));

    api.yesterdayWinner()
      .then((data) => setYesterdayWinner(data.player_name ? data : null))
      .catch(() => setYesterdayWinner(null));
  }, []);

  const isMegaMonday = dailyGameMode === 'mega_monday';
  const isPointsMode = dailyGameMode === 'points';

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!playerName.trim()) {
      setError('Enter a player name');
      return;
    }

    try {
      if (password) {
        const result = await api.verifyName(playerName, password);
        if (result.success) {
          localStorage.setItem('playerName', playerName.trim());
          localStorage.setItem('buildpro_player_name', playerName.trim());
          onLogin(playerName);
        } else {
          setError('Invalid player name or password');
        }
      } else {
        localStorage.setItem('playerName', playerName.trim());
        localStorage.setItem('buildpro_player_name', playerName.trim());
        onLogin(playerName);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReserveName = async (e) => {
    e.preventDefault();
    setError('');

    if (!playerName.trim() || !password.trim()) {
      setError('Enter player name and password');
      return;
    }

    setIsReserving(true);

    try {
      const result = await api.reserveName(playerName, password);
      if (result.success) {
        localStorage.setItem('playerName', playerName.trim());
        localStorage.setItem('buildpro_player_name', playerName.trim());
        onLogin(playerName);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsReserving(false);
    }
  };

  if (hasPlayedToday && (appPlayerName || playerName)) {
    return <AlreadyPlayedPage
      game={BUILDPRO_GAME}
      title="You already played today!"
      message="Come back tomorrow for a new puzzle."
      leaderboardHref="/leaderboard"
      homeHref={hubUrl()}
    />;
  }

  return (
    <div className="start-screen">
      <div className="start-container">
        <BuildProBrand size="login" className="start-brand" />

        <p>{isPointsMode ? 'Place letters on the grid to build the highest-scoring words.' : 'Place letters on the grid to build words. Race against the clock!'}</p>

        {isMegaMonday && (
          <div className="mega-monday-callout">
            <div className="mega-monday-title">Mega Monday</div>
            <div className="mega-monday-copy">All 20 letters are live from the start. Swap, build, and race the clock.</div>
          </div>
        )}

        {isPointsMode && (
          <div className="mega-monday-callout">
            <div className="mega-monday-title">Points Friday</div>
            <div className="mega-monday-copy">
              <strong>Today&apos;s goal is highest score, not fastest time.</strong><br />
              All 24 letters are live from the start. No swapping. Words score 1 point for 2–4 letters, 2 for 5, 3 for 6, 5 for 7, and 11 for 8+.
            </div>
          </div>
        )}

        {(todayLeader || yesterdayWinner) && (
          <div className="start-leader-summary">
            {todayLeader && (
              <div className="start-leader-card start-leader-card-current">
                <span>Leading today</span>
                <strong>{todayLeader.player_name}</strong>
                <small>{todayLeader.game_mode === 'points' ? `${todayLeader.points || 0} points` : formatTime(todayLeader.time_taken)}</small>
              </div>
            )}
            {yesterdayWinner && (
              <div className="start-leader-card">
                <span>Yesterday</span>
                <strong>{yesterdayWinner.player_name}</strong>
                <small>{yesterdayWinner.game_mode === 'points' ? `${yesterdayWinner.points || 0} points` : formatTime(yesterdayWinner.time_taken)}</small>
              </div>
            )}
          </div>
        )}

        {!isAuthenticated ? (
          <form onSubmit={handleLogin} className="start-form">
            <input
              type="text"
              placeholder="Player name"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              className="input-field"
            />

            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field"
            />

            {error && <div className="error-message">{error}</div>}

            <div className="button-group">
              <button type="submit" className="btn btn-primary" style={{ backgroundColor: BUILDPRO_COLOR, boxShadow: `0 2px 0 ${BUILDPRO_SHADOW}` }}>
                Play BuildPro
              </button>
              <button
                type="button"
                onClick={handleReserveName}
                disabled={isReserving}
                className="btn btn-secondary"
              >
                {isReserving ? 'Reserving...' : 'Reserve Name'}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="start-session">
              <div className="start-session-copy">
                <p className="start-session-label">Signed in as</p>
                <p className="start-session-name">{appPlayerName || playerName}</p>
              </div>

              {hasPlayedToday ? (
                <div className="start-played">You already played today. Come back tomorrow.</div>
              ) : (
                <div className="start-ready">
                  {isMegaMonday ? 'Ready for Mega Monday.' : isPointsMode ? 'Ready to play for points.' : 'Ready to start today\'s board.'}
                </div>
              )}
            </div>

            <div className="button-group">
              <button
                type="button"
                onClick={onStartGame}
                disabled={checkingPlayed || hasPlayedToday}
                className="btn btn-primary"
                style={{ backgroundColor: BUILDPRO_COLOR, boxShadow: `0 2px 0 ${BUILDPRO_SHADOW}` }}
              >
                {isPointsMode ? 'Start Points Puzzle' : 'Start Game'}
              </button>
            </div>
          </>
        )}


      </div>
    </div>
  );
}

export default StartScreen;
